document.addEventListener('DOMContentLoaded', () => {
  const filterForm = document.getElementById('CollectionFiltersForm');
  if (!filterForm) return;

  const productGrid = document.getElementById('HousiousProductGrid');
  const gridContainer = document.getElementById('ProductGridContainer');
  const loadedCountEl = document.getElementById('LoadedProductsCount');
  const totalCountEl = document.getElementById('ShowingProductsTotal');

  const defaultCollectionHandle = filterForm.dataset.currentCollectionHandle || '';
  let defaultCollectionUrl = filterForm.dataset.currentCollectionUrl || window.location.pathname;
  if (window.location.pathname.includes('all-collections')) {
    defaultCollectionUrl = '/collections/all';
  }

  const SECTION_ID = 'main-collection-product-grid';

  let filterAbortController = null;
  let isLoadingMore = false;
  let scrollObserver = null;

  // Map of collectionUrl -> nextPageUrl. Drives "load more" across every
  // collection that is currently part of the combined result set.
  let activeNextUrls = {};

  // Tracks which set of collections the current price bounds were computed
  // for. Bounds only get recalculated when this set changes — not on every
  // price-slider drag or availability checkbox click.
  let boundsComputedForKey = null;

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  // Ground-truth product count: count actual rendered cards rather than
  // guessing at an offset from raw children/keys. Previously this used a
  // hardcoded "- 2" subtraction that had no explanation and could drift
  // (or go negative) if the DOM structure ever changed.
  function countProductCards() {
    if (!productGrid) return 0;
    return productGrid.querySelectorAll('.product-item-card').length;
  }

  function getSelectedCollections() {
    const checked = Array.from(filterForm.querySelectorAll('.dynamic-cat-nav:checked'));
    if (checked.length === 0) {
      return [{ handle: defaultCollectionHandle, url: defaultCollectionUrl }];
    }
    return checked.map((cb) => ({
      handle: cb.dataset.collectionHandle,
      url: cb.dataset.collectionUrl
    }));
  }

  // Everything in the form EXCEPT the category checkboxes (they have no
  // "name" attribute on purpose, so FormData already skips them).
  // The price range sliders and the MIN/MAX number boxes intentionally
  // share the same "name" so Shopify accepts either — but that means
  // FormData yields two entries per price field. Collapse duplicates to
  // one value per key so the outgoing/address-bar URL stays clean.
  function buildBaseParams() {
    const raw = new FormData(filterForm);
    const deduped = new URLSearchParams();
    const seen = new Set();
    for (const [key, value] of raw.entries()) {
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.set(key, value);
    }
    return deduped;
  }

  function withSection(url) {
    return url + (url.includes('?') ? '&' : '?') + 'section_id=' + SECTION_ID;
  }

  function uniqueKeyForCard(card) {
    const link = card.querySelector && card.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href').split('?')[0] : card.innerHTML.slice(0, 80);
  }

  function mergeCardsInto(existingKeys, sourceGrid, targetFragment) {
    if (!sourceGrid) return;
    Array.from(sourceGrid.children).forEach((card) => {
      const key = uniqueKeyForCard(card);
      if (!existingKeys.has(key)) {
        existingKeys.add(key);
        targetFragment.appendChild(card.cloneNode(true));
      }
    });
  }

  function parseTotalFromDoc(doc) {
    const el = doc.getElementById('ShowingProductsTotal');
    if (!el) return 0;
    const match = el.textContent.match(/[\d,]+/);
    return match ? parseInt(match[0].replace(/,/g, ''), 10) : 0;
  }

  function readPriceBoundsFromDoc(doc) {
    const block = doc.querySelector('.dynamic-price-range-canva-block');
    if (!block) return null;
    const min = parseFloat(block.dataset.priceMin);
    const max = parseFloat(block.dataset.priceMax);
    if (isNaN(min) || isNaN(max)) return null;
    return { min, max };
  }

  // ---------------------------------------------------------------------
  // Price slider
  // ---------------------------------------------------------------------

  function initPriceSliderFramework() {
    const rangeMinInput = filterForm.querySelector('.range-min');
    const rangeMaxInput = filterForm.querySelector('.range-max');
    const numMinInput = filterForm.querySelector('.dynamic-min-input');
    const numMaxInput = filterForm.querySelector('.dynamic-max-input');
    const sliderTrack = filterForm.querySelector('.slider-track');

    if (!rangeMinInput || !rangeMaxInput || !sliderTrack) return;

    function updateSliderTrack() {
      const minLimit = parseFloat(rangeMinInput.min) || 0;
      const maxLimit = parseFloat(rangeMinInput.max) || 500;

      let minVal = parseFloat(rangeMinInput.value);
      let maxVal = parseFloat(rangeMaxInput.value);

      if (minVal > maxVal) {
        minVal = maxVal;
        rangeMinInput.value = minVal;
      }

      const percentMin = ((minVal - minLimit) / (maxLimit - minLimit)) * 100;
      const percentMax = ((maxVal - minLimit) / (maxLimit - minLimit)) * 100;

      sliderTrack.style.left = percentMin + '%';
      sliderTrack.style.right = (100 - percentMax) + '%';

      if (numMinInput) numMinInput.value = minVal;
      if (numMaxInput) numMaxInput.value = maxVal;
    }

    updateSliderTrack();

    rangeMinInput.oninput = () => {
      if (parseFloat(rangeMinInput.value) > parseFloat(rangeMaxInput.value)) {
        rangeMinInput.value = rangeMaxInput.value;
      }
      updateSliderTrack();
    };

    rangeMaxInput.oninput = () => {
      if (parseFloat(rangeMaxInput.value) < parseFloat(rangeMinInput.value)) {
        rangeMaxInput.value = rangeMinInput.value;
      }
      updateSliderTrack();
    };

    rangeMinInput.onchange = renderFilteredPage;
    rangeMaxInput.onchange = renderFilteredPage;

    // NEW: typing an exact price directly into the MIN/MAX number boxes
    // previously did nothing — only dragging the tiny slider handle
    // triggered a search. On mobile, dragging two overlapping handles
    // precisely is hard, so typing a number should work just as well.
    if (numMinInput) {
      numMinInput.onchange = () => {
        const minLimit = parseFloat(rangeMinInput.min) || 0;
        let val = parseFloat(numMinInput.value);
        if (isNaN(val)) val = minLimit;
        val = Math.max(minLimit, Math.min(val, parseFloat(rangeMaxInput.value)));
        numMinInput.value = val;
        rangeMinInput.value = val;
        updateSliderTrack();
        renderFilteredPage();
      };
    }

    if (numMaxInput) {
      numMaxInput.onchange = () => {
        const maxLimit = parseFloat(rangeMaxInput.max) || 500;
        let val = parseFloat(numMaxInput.value);
        if (isNaN(val)) val = maxLimit;
        val = Math.min(maxLimit, Math.max(val, parseFloat(rangeMinInput.value)));
        numMaxInput.value = val;
        rangeMaxInput.value = val;
        updateSliderTrack();
        renderFilteredPage();
      };
    }
  }

  // Rewrites the slider's min/max bounds (e.g. after combining two
  // collections) without throwing away the customer's current selection,
  // unless that selection now falls outside the new bounds.
  function applyPriceBounds(bounds) {
    if (!bounds) return;

    const rangeMinInput = filterForm.querySelector('.range-min');
    const rangeMaxInput = filterForm.querySelector('.range-max');
    const numMinInput = filterForm.querySelector('.dynamic-min-input');
    const numMaxInput = filterForm.querySelector('.dynamic-max-input');
    const block = filterForm.querySelector('.dynamic-price-range-canva-block');

    [rangeMinInput, rangeMaxInput, numMinInput, numMaxInput].forEach((input) => {
      if (!input) return;
      input.min = bounds.min;
      input.max = bounds.max;
    });

    if (block) {
      block.dataset.priceMin = bounds.min;
      block.dataset.priceMax = bounds.max;
    }

    if (rangeMinInput && parseFloat(rangeMinInput.value) < bounds.min) {
      rangeMinInput.value = bounds.min;
    }
    if (rangeMaxInput && parseFloat(rangeMaxInput.value) > bounds.max) {
      rangeMaxInput.value = bounds.max;
    }

    initPriceSliderFramework();
  }

  // ---------------------------------------------------------------------
  // Core AJAX render — fetches one or many collections and combines them
  // ---------------------------------------------------------------------

  function renderFilteredPage() {
    if (filterAbortController) filterAbortController.abort();
    filterAbortController = new AbortController();
    const signal = filterAbortController.signal;

    const collections = getSelectedCollections();
    const collectionsKey = collections.map((c) => c.handle).sort().join(',');
    const boundsNeedRecompute = collectionsKey !== boundsComputedForKey;
    const baseParams = buildBaseParams();

    // Keep the address bar shareable. Single collection -> its own clean
    // URL, like before. Multiple collections -> add a "collections" param
    // so a reload/back-button restores the same combined selection.
    let addressUrl;
    if (collections.length === 1) {
      addressUrl = collections[0].url + '?' + baseParams.toString();
    } else {
      const combinedParams = new URLSearchParams(baseParams);
      combinedParams.set('collections', collections.map((c) => c.handle).join(','));
      addressUrl = defaultCollectionUrl + '?' + combinedParams.toString();
    }
    // Use replaceState (not pushState) for filter/sort changes: the URL
    // still stays shareable/reloadable, but every checkbox tap no longer
    // creates its own browser-history entry. Without this, a customer who
    // taps 3 filters has to hit the mobile back button 3 times just to
    // get back to where they started.
    history.replaceState({ path: addressUrl }, '', addressUrl);

    activeNextUrls = {};

    const fetches = collections.map((col) => {
      const url = col.url + '?' + baseParams.toString();
      return fetch(withSection(url), { signal })
        .then((r) => r.text())
        .then((html) => ({ col, doc: new DOMParser().parseFromString(html, 'text/html') }));
    });

    Promise.all(fetches)
      .then((results) => {
        const existingKeys = new Set();
        const fragment = document.createDocumentFragment();
        let totalCount = 0;
        let bestMin = Infinity;
        let bestMax = -Infinity;

        results.forEach(({ col, doc }) => {
          const sourceGrid = doc.getElementById('HousiousProductGrid');
          mergeCardsInto(existingKeys, sourceGrid, fragment);

          totalCount += parseTotalFromDoc(doc);

          if (boundsNeedRecompute) {
            const bounds = readPriceBoundsFromDoc(doc);
            if (bounds) {
              bestMin = Math.min(bestMin, bounds.min);
              bestMax = Math.max(bestMax, bounds.max);
            }
          }

          const nextLoader = doc.getElementById('InfiniteScrollLoader');
          if (nextLoader && nextLoader.dataset.nextUrl) {
            activeNextUrls[col.url] = nextLoader.dataset.nextUrl;
          }
        });

        if (productGrid) {
          productGrid.innerHTML = '';
          productGrid.appendChild(fragment);
          reinitializeProductCards();
        }

        const loadedCount = countProductCards();
        if (loadedCountEl) loadedCountEl.textContent = loadedCount;

        // "Total" is tricky with multiple collections: each collection's
        // own count includes any product that also lives in another
        // selected collection, so summing them can overcount by exactly
        // the number of shared products. While more pages are still to
        // load, show the summed figure as an upper-bound estimate; once
        // every page from every selected collection is in hand, we know
        // the real deduplicated number, so use that instead.
        const hasMorePages = Object.keys(activeNextUrls).length > 0;
        const displayTotal = hasMorePages ? Math.max(totalCount, loadedCount) : loadedCount;
        if (totalCountEl) totalCountEl.textContent = 'of ' + displayTotal;

        // Bounds are fixed per collection-selection: only recompute them
        // when the set of checked collections actually changed. A price
        // drag or an availability toggle within the same collection(s)
        // must NOT move the min/max again.
        if (boundsNeedRecompute) {
          if (bestMin !== Infinity && bestMax !== -Infinity) {
            applyPriceBounds({ min: bestMin, max: bestMax });
          }
          boundsComputedForKey = collectionsKey;
        } else {
          initPriceSliderFramework();
        }

        syncInfiniteScrollLoader();
      })
      .catch((err) => {
        if (err.name !== 'AbortError') console.log(err);
      });
  }

  // ---------------------------------------------------------------------
  // Infinite scroll — now aware of every active collection at once
  // ---------------------------------------------------------------------

  function observeLoader(loaderEl) {
    if (scrollObserver) scrollObserver.disconnect();
    scrollObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadMoreProducts();
        });
      },
      { rootMargin: '400px' }
    );
    scrollObserver.observe(loaderEl);
  }

  function syncInfiniteScrollLoader() {
    let loader = document.getElementById('InfiniteScrollLoader');
    const hasMore = Object.keys(activeNextUrls).length > 0;

    if (!hasMore) {
      if (loader) loader.remove();
      if (scrollObserver) scrollObserver.disconnect();
      return;
    }

    if (!loader && gridContainer) {
      loader = document.createElement('div');
      loader.id = 'InfiniteScrollLoader';
      loader.innerHTML =
        '<div class="loader-content"><div class="circular-spinner"></div>' +
        '<span class="loader-text">Loading products...</span></div>';
      gridContainer.appendChild(loader);
    }

    if (loader) observeLoader(loader);
  }

  function reinitializeProductCards() {
  if (!productGrid) return;

  /*
   * Product cards loaded through fetch()/cloneNode() do not retain
   * JavaScript event listeners from the original DOM.
   *
   * Notify the existing product-card/theme JavaScript that new cards
   * have been inserted.
   */
  document.dispatchEvent(
    new CustomEvent('productCards:updated', {
      detail: {
        container: productGrid
      }
    })
  );

  /*
   * Also notify Shopify/theme section listeners.
   */
  document.dispatchEvent(
    new CustomEvent('shopify:section:load', {
      detail: {
        sectionId: SECTION_ID,
        container: productGrid
      }
    })
  );
}
  async function loadMoreProducts() {
    if (isLoadingMore || Object.keys(activeNextUrls).length === 0) return;
    isLoadingMore = true;

    const entries = Object.entries(activeNextUrls);
    const existingKeys = new Set(Array.from(productGrid.children).map(uniqueKeyForCard));
    const fragment = document.createDocumentFragment();

    try {
      await Promise.all(
        entries.map(async ([collectionUrl, nextUrl]) => {
          const response = await fetch(withSection(nextUrl));
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const sourceGrid = doc.getElementById('HousiousProductGrid');
          mergeCardsInto(existingKeys, sourceGrid, fragment);

          const nextLoader = doc.getElementById('InfiniteScrollLoader');
          if (nextLoader && nextLoader.dataset.nextUrl) {
            activeNextUrls[collectionUrl] = nextLoader.dataset.nextUrl;
          } else {
            delete activeNextUrls[collectionUrl];
          }
        })
      );

      if (productGrid) {
        productGrid.appendChild(fragment);

        // Reinitialize newly loaded product-card arrow/swiper events
        reinitializeProductCards();
      }

      const loadedCount = countProductCards();
      if (loadedCountEl) loadedCountEl.textContent = loadedCount;

      // Once every collection has run out of next pages, we've now seen
      // every product — correct the total to the exact deduplicated count.
      if (Object.keys(activeNextUrls).length === 0 && totalCountEl) {
        totalCountEl.textContent = 'of ' + loadedCount;
      }

      syncInfiniteScrollLoader();
    } catch (error) {
      console.error('Error auto-fetching products:', error);
    } finally {
      isLoadingMore = false;
    }
  }

  const initialLoader = document.getElementById('InfiniteScrollLoader');
  if (initialLoader && initialLoader.dataset.nextUrl) {
    activeNextUrls[defaultCollectionUrl] = initialLoader.dataset.nextUrl;
    observeLoader(initialLoader);
  }

  // ---------------------------------------------------------------------
  // Event delegation
  // ---------------------------------------------------------------------

  document.body.addEventListener('click', (e) => {
    if (e.target.closest('.mobile-filter-toggle-btn')) {
      document.querySelector('.collection-filters-sidebar').classList.add('drawer-open');
      document.querySelector('.filter-drawer-overlay').classList.add('active');
    }

    if (e.target.classList.contains('filter-drawer-overlay')) {
      document.querySelector('.collection-filters-sidebar').classList.remove('drawer-open');
      document.querySelector('.filter-drawer-overlay').classList.remove('active');
    }

    if (e.target.closest('#MobileFilterCloseBtn')) {
      document.querySelector('.collection-filters-sidebar').classList.remove('drawer-open');
      document.querySelector('.filter-drawer-overlay').classList.remove('active');
    }

    const viewMoreBtn = e.target.closest('#CategoryViewMoreBtn');
    if (viewMoreBtn) {
      e.preventDefault();
      const filterBlock = viewMoreBtn.closest('.custom-navigation-categories');
      const hiddenItems = filterBlock.querySelectorAll('.hidden-category-item');
      let isExpanded = viewMoreBtn.getAttribute('data-expanded') === 'true';
      isExpanded = !isExpanded;
      hiddenItems.forEach((item) => (item.style.display = isExpanded ? 'block' : 'none'));
      viewMoreBtn.setAttribute('data-expanded', isExpanded ? 'true' : 'false');
      viewMoreBtn.textContent = isExpanded
        ? '- View Less'
        : `+ View More (${viewMoreBtn.getAttribute('data-count')})`;
    }

    if (e.target.id === 'ClearAllFilters') {
      e.preventDefault();
      filterForm.reset();

      // form.reset() also unchecks category boxes — restore only the
      // collection the customer is actually standing on.
      filterForm.querySelectorAll('.dynamic-cat-nav').forEach((cb) => {
        cb.checked = cb.dataset.collectionHandle === defaultCollectionHandle;
      });

      const rangeMin = filterForm.querySelector('.range-min');
      const rangeMax = filterForm.querySelector('.range-max');
      const numMin = filterForm.querySelector('.dynamic-min-input');
      const numMax = filterForm.querySelector('.dynamic-max-input');

      if (rangeMin) rangeMin.value = rangeMin.min;
      if (rangeMax) rangeMax.value = rangeMax.max;
      if (numMin) numMin.value = rangeMin ? rangeMin.min : 0;
      if (numMax) numMax.value = rangeMax ? rangeMax.max : 500;

      initPriceSliderFramework();
      renderFilteredPage();
    }

    const paginationLink = e.target.closest('.pagination a, .pagination-wrapper a');
    if (paginationLink) {
      e.preventDefault();
      const url = new URL(paginationLink.href);
      history.pushState({ path: url.href }, '', url.href);

      fetch(withSection(url.href))
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newGrid = doc.getElementById('HousiousProductGrid');
          if (productGrid && newGrid) productGrid.innerHTML = newGrid.innerHTML;
          initPriceSliderFramework();
        });
    }
  });

  filterForm.addEventListener('change', (e) => {
    if (
      e.target.matches('.filter-checkbox-input') ||
      e.target.matches('.dynamic-cat-nav') ||
      e.target.matches('input[name="filter.v.availability"]') ||
      e.target.matches('input[type="checkbox"]') ||
      e.target.id === 'SortBySelect'
    ) {
      renderFilteredPage();
    }
  });

  // Restore a combined selection from the URL (e.g. after a page reload
  // or the browser back button lands on a ?collections=a,b link).
  (function restoreFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const collectionsParam = params.get('collections');
    if (!collectionsParam) return;

    const handles = collectionsParam.split(',');
    let changed = false;
    filterForm.querySelectorAll('.dynamic-cat-nav').forEach((cb) => {
      const shouldCheck = handles.includes(cb.dataset.collectionHandle);
      if (cb.checked !== shouldCheck) {
        cb.checked = shouldCheck;
        changed = true;
      }
    });

    if (changed) renderFilteredPage();
  })();

  initPriceSliderFramework();
});
